import {
  Box,
  ButtonBase,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

import { AddIcon, CloseIcon } from "@/icons";

export interface VariationTabItem {
  /** Stable variation id (used as the React key). */
  id: string;
  label: string;
  /** Whether this tab shows a remove (×) affordance. */
  removable: boolean;
}

export interface VariationTabsProps {
  items: VariationTabItem[];
  activeIndex: number;
  onSelect: (index: number) => void;
  /** Omit to hide the "+ add variant" affordance entirely. */
  onAdd?: () => void;
  onRemove?: (index: number) => void;
  /** When false, the add button is shown disabled (not hidden) with a tooltip. */
  canAdd?: boolean;
  /** Tooltip shown on the add button when `canAdd` is false. */
  addDisabledReason?: string;
  /** Index of a tab performing async work (shows a spinner). */
  busyIndex?: number | null;
  /** Show a spinner on the add button while a new variation is being created. */
  addBusy?: boolean;
  /** Render the whole strip read-only (no add/remove). */
  disabled?: boolean;
}

/**
 * Browser-tab-style variation selector. Each variation is a card; a trailing
 * "+ add variant" affordance adds one (capped by `canAdd`), and removable cards
 * expose a × to delete. Channel-agnostic and fully prop-driven so both the
 * email (Zustand) and SMS flows can use it.
 */
export function VariationTabs({
  items,
  activeIndex,
  onSelect,
  onAdd,
  onRemove,
  canAdd = true,
  addDisabledReason,
  busyIndex,
  addBusy = false,
  disabled = false,
}: VariationTabsProps) {
  return (
    <Stack
      direction="row"
      role="tablist"
      aria-label="Variations"
      gap={1}
      alignItems="flex-end"
      sx={{ flexWrap: "wrap" }}
    >
      {items.map((item, index) => {
        const isActive = index === activeIndex;
        const isBusy = busyIndex === index;
        return (
          <ButtonBase
            key={item.id}
            role="tab"
            aria-selected={isActive}
            onClick={() => onSelect(index)}
            sx={(theme) => ({
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 3,
              py: 2,
              maxWidth: 220,
              borderTopLeftRadius: theme.shape.borderRadiusMd,
              borderTopRightRadius: theme.shape.borderRadiusMd,
              border: `1px solid ${theme.palette.tokens.outlines.defaultBordersLight}`,
              backgroundColor: isActive
                ? theme.palette.background["paper-elevation-1"]
                : theme.palette.background["paper-elevation-0"],
              color: isActive ? "text.primary" : "text.secondary",
              transition: theme.transitions.create([
                "background-color",
                "color",
              ]),
            })}
          >
            {isBusy && <CircularProgress size={14} thickness={5} />}
            <Typography variant="body1" noWrap sx={{ p: 2 }}>
              {item.label}
            </Typography>
            {item.removable && !disabled && onRemove && (
              <IconButton
                component="span"
                size="small"
                aria-label={`Remove ${item.label}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(index);
                }}
                sx={{ p: "2px" }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </ButtonBase>
        );
      })}

      {onAdd && !disabled && (
        <Tooltip title={!canAdd && addDisabledReason ? addDisabledReason : ""}>
          <Box component="span">
            <ButtonBase
              aria-label="Add variant"
              disabled={!canAdd || addBusy}
              onClick={onAdd}
              sx={(theme) => ({
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 3,
                py: 2,
                borderTopLeftRadius: theme.shape.borderRadiusMd,
                borderTopRightRadius: theme.shape.borderRadiusMd,
                border: `1px dashed ${theme.palette.tokens.outlines.defaultBordersLight}`,
                color: "text.secondary",
                "&.Mui-disabled": { opacity: 0.5 },
              })}
            >
              {addBusy ? (
                <CircularProgress size={14} thickness={5} />
              ) : (
                <AddIcon fontSize="small" />
              )}
              <Typography variant="body1" sx={{ p: 2 }}>
                add variant
              </Typography>
            </ButtonBase>
          </Box>
        </Tooltip>
      )}
    </Stack>
  );
}
